import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import RelatedServicesNav from '../components/RelatedServicesNav';
import StickyMobileCta from '../components/StickyMobileCta';
import {
  Stethoscope, Syringe, Shield, ClipboardCheck, ScanLine, FlaskConical, Bandage,
  Apple, HeartPulse, Baby, Sparkles, Microscope, Ear, Bug, Activity, Smile, Eye,
  MapPin, Phone, MessageCircle, CalendarDays, ArrowRight, Users, ClipboardList,
  BookOpen, HandHeart, ShieldCheck, Clock, MessageSquare, Handshake, Award,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { CONTACT, GOOGLE_RATING } from '../data/mock';
import { cdnImg } from '../utils/cdn';
import { trackCallClick, trackWhatsAppClick, trackBookButtonClick } from '../lib/analytics';

const ASSET = 'https://customer-assets.emergentagent.com/job_petcare-preview-1/artifacts';
const DR_PORTRAIT = `${ASSET}/jnhxjj9q_WhatsApp%20Image%202026-07-08%20at%2012.31.40.jpeg`;
const HERO_IMAGE = 'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?auto=format&fit=crop&w=1400&q=80';

const TITLE = 'Pet Doctor in Noida | Trusted Family Veterinarian | Ichi & Ori Pet Care';
const DESCRIPTION = 'Looking for a trusted pet doctor in Noida? Dr Kaushal at Ichi & Ori Pet Care offers unhurried consultations, honest advice, preventive care, diagnostics and long-term veterinary support for dogs, cats and small pets.';
const CANONICAL = 'https://ichiandoripetcare.com/pet-doctor-noida';
const set = (sel, attr, val) => { const el = document.head.querySelector(sel); if (el) el.setAttribute(attr, val); };

const WHAT_A_PET_DOCTOR_DOES = [
  { Icon: Stethoscope, title: 'Listens before prescribing',     text: 'A good pet doctor asks the questions no one else asked, watches how your pet moves and behaves, and only then decides what is truly needed.' },
  { Icon: BookOpen,    title: 'Explains the diagnosis clearly', text: 'You leave the room understanding what is going on with your pet, what the plan is and what the next steps look like, in plain language.' },
  { Icon: Handshake,   title: 'Respects your budget',           text: 'Honest advice on which tests, medications and procedures are essential today, and which can be paced or planned over time.' },
  { Icon: Clock,       title: 'Stays with you across years',    text: 'From puppyhood or kittenhood into senior years, a family vet keeps notes, remembers your pet and adjusts care as life changes.' },
  { Icon: Shield,      title: 'Puts prevention first',          text: 'Timely vaccinations, deworming, dental care and nutrition catch problems before they turn into emergencies and expensive treatments.' },
  { Icon: HandHeart,   title: 'Treats every pet as family',     text: 'Every recommendation is the one the doctor would make if your pet were their own, no upselling, no scare tactics.' },
];

const SERVICES = [
  { Icon: ClipboardCheck, title: 'Consultations',            text: 'Unhurried appointments where we examine, question and think, so recommendations are tailored to your specific pet, not a generic template.' },
  { Icon: Syringe,        title: 'Vaccinations',             text: 'Age-appropriate core and lifestyle vaccines for dogs and cats, planned around exposure, history and current health.' },
  { Icon: Baby,           title: 'Puppy & Kitten Care',      text: 'Nutrition, deworming, socialisation and the confident foundation every young pet deserves in the first few months at home.' },
  { Icon: HeartPulse,     title: 'Adult Wellness',           text: 'Annual health checks that keep active adult dogs and cats on top of vaccines, weight, dental care and early illness screening.' },
  { Icon: Microscope,     title: 'Senior Care',              text: 'Slower, gentler consultations with screening bloodwork and pain assessment, tuned to the changing needs of older pets.' },
  { Icon: Sparkles,       title: 'Skin, Coat & Ear Issues',  text: 'Itching, redness, hair loss and ear problems investigated properly, so we treat the cause and not just the symptom.' },
  { Icon: Apple,          title: 'Digestive Concerns',       text: 'A structured approach to vomiting, diarrhea, appetite loss and food sensitivities, from diet trials to targeted diagnostics.' },
  { Icon: Bug,            title: 'Parasite Prevention',      text: 'Season-aware plans for ticks, fleas and worms, and prompt treatment for tick fever and related conditions common in NCR.' },
  { Icon: Smile,          title: 'Dental & Oral Health',     text: 'Oral exams, dental care planning and treatment guidance for one of the most underdiagnosed causes of quiet suffering in pets.' },
  { Icon: Bandage,        title: 'Minor Procedures',         text: 'Wound care, abscess drainage, ear flushes and other in-clinic procedures handled with careful pain relief and clear aftercare.' },
  { Icon: FlaskConical,   title: 'Diagnostics',              text: 'In-house CBC, ECG, blood pressure and microscopy, with accredited partner labs for biochemistry, urine and histopathology.' },
  { Icon: ScanLine,       title: 'Digital X-Ray',            text: 'Direct-radiography imaging reviewed on screen within minutes for orthopaedic, chest and abdominal concerns.' },
];

const SIGNS_TO_VISIT = [
  { Icon: Apple,        title: 'Appetite changes',         text: 'Skipping meals for a day, unusual hunger or sudden weight loss are among the most reliable early signals that something is off.' },
  { Icon: HeartPulse,   title: 'Vomiting or diarrhea',     text: 'Occasional episodes may be diet-related, but repeated, bloody or forceful cases always warrant a professional look, sooner rather than later.' },
  { Icon: Sparkles,     title: 'Persistent scratching',    text: 'Constant licking, biting or a dull, flaky coat usually points to allergies, parasites or a skin infection that will not resolve on its own.' },
  { Icon: Ear,          title: 'Head shaking or ear odour',text: 'Ear infections progress fast in dogs and cats. Early treatment is far kinder and more effective than waiting it out.' },
  { Icon: Activity,     title: 'Limping or stiffness',     text: 'A subtle change in gait, difficulty on stairs or reluctance to jump is often the first sign of an orthopaedic or joint issue.' },
  { Icon: Eye,          title: 'Red or watery eyes',       text: 'Eye issues can worsen within hours. Any redness, squinting, cloudiness or discharge deserves a same-day check.' },
  { Icon: Stethoscope,  title: 'Coughing or breathing changes', text: 'Kennel cough, allergies, heart disease and respiratory infections all start with subtle breathing changes worth investigating early.' },
  { Icon: ClipboardList,title: 'Behaviour changes',        text: 'Sudden hiding, aggression, restlessness or clinginess is usually a medical clue, not a mood. We take these seriously.' },
  { Icon: Syringe,      title: 'Overdue vaccinations',     text: 'Even one missed booster can leave your pet exposed. A quick review sets the schedule back on track without unnecessary repeat doses.' },
];

const LIFE_STAGES = [
  {
    Icon: Baby,
    tag: 'FIRST YEAR',
    title: 'Puppies & Kittens',
    text: 'The first months at home shape everything that follows. We build a calm vaccination and deworming schedule, guide you on nutrition and portion sizes, and use short, positive visits to make future clinic trips easier.',
    bullets: ['First health check and questions', 'Vaccination and deworming plan', 'Nutrition and portioning advice', 'Socialisation and handling tips'],
  },
  {
    Icon: Activity,
    tag: 'YEARS 2 TO 7',
    title: 'Active Adult Pets',
    text: 'The routine years benefit most from consistency. An annual wellness visit keeps vaccines, weight, dental care and skin issues on track, and quietly prevents most of the problems that show up later in life.',
    bullets: ['Annual physical and vaccine review', 'Weight and dental assessment', 'Parasite prevention updates', 'Diet and lifestyle check-in'],
  },
  {
    Icon: Award,
    tag: 'AGE 8 AND BEYOND',
    title: 'Senior Companions',
    text: 'Older pets change quickly, so visits move to twice a year with screening bloodwork, pain assessment and mobility support. Small, timely adjustments in these years often add the most comfortable time to their lives.',
    bullets: ['Twice-yearly checkups', 'Screening bloodwork and urine tests', 'Joint and mobility support', 'Comfort-focused pain management'],
  },
];

const FIRST_VISIT = [
  { step: '01', title: 'Warm welcome',        text: 'A calm reception, low-stress waiting and time for your pet to settle before anything else happens.' },
  { step: '02', title: 'Listening first',     text: 'A focused conversation about diet, behaviour, history and the specific reason you came in today.' },
  { step: '03', title: 'Nose-to-tail exam',   text: 'A careful physical examination covering weight, coat, eyes, ears, mouth, heart, lungs, abdomen and joints.' },
  { step: '04', title: 'Honest plan',         text: 'A clear plan with reasoning, expected costs and any tests that are truly needed, in the priority they should happen.' },
  { step: '05', title: 'Aftercare guidance',  text: 'Written notes, home-care instructions and a clear line to reach us if anything changes after you leave.' },
];

const WHY_FAMILIES = [
  'Time to actually listen',
  'One trusted doctor across visits',
  'Honest advice, no upselling',
  'Transparent costs before you decide',
  'In-house diagnostics for faster answers',
  'Calm, low-stress consultation rooms',
  'Follow-up messages and check-ins',
  'A doctor who remembers your pet',
];

const PROMISES = [
  { Icon: ShieldCheck,    title: 'Honesty above everything',  text: 'You will always hear what your pet actually needs, and what can safely wait, before any test or treatment is added to the plan.' },
  { Icon: MessageSquare,  title: 'Clear communication',       text: 'No jargon, no rushed explanations. You leave every consultation knowing exactly what is happening and why.' },
  { Icon: HandHeart,      title: 'Compassion by default',     text: 'Anxious pets, nervous parents, complicated histories, all handled with patience and the same kindness we would want for our own family.' },
  { Icon: ClipboardCheck, title: 'Continuity of care',        text: 'One doctor, one detailed record, one long-term plan. Your pet is not a new file every time you visit.' },
];

const LOCATIONS = [
  'Sector 90', 'Sector 137', 'Sector 143', 'Sector 168', 'Sector 93', 'Sector 100',
  'Sector 128', 'Noida Expressway', 'Nearby residential societies',
];

const FAQS = [
  { q: 'What does a pet doctor actually do?', a: 'A pet doctor, more formally a veterinarian, is trained to examine, diagnose and treat medical conditions across species. Beyond illness care, a good family pet doctor also guides preventive health, nutrition, vaccinations, dental care and behavioural questions, and stays with your pet across every life stage.' },
  { q: 'How do I choose a good pet doctor in Noida?', a: 'Look for a veterinarian who takes time to listen, explains diagnosis in plain language, offers honest advice on costs, and prioritises prevention over unnecessary procedures. Reading recent reviews from other pet parents and visiting the clinic once yourself is often the clearest indicator.' },
  { q: 'How often should my pet see a doctor?', a: 'Healthy adult dogs and cats do well with an annual wellness visit. Puppies and kittens need visits every few weeks in their first four months, and senior pets typically benefit from twice-yearly checkups with basic screening bloodwork.' },
  { q: 'When is it time to visit the vet urgently?', a: 'Any difficulty breathing, repeated vomiting, blood in urine or stool, sudden collapse, severe limping, unresponsive behaviour or suspected poisoning should be seen the same day. If you are unsure, call or WhatsApp us. A short conversation usually makes the right next step clear.' },
  { q: 'Do you treat both dogs and cats?', a: 'Yes. Dr Kaushal treats dogs, cats, rabbits and other small companion animals. The clinic is set up with calm, separate handling for cats to keep visits as low-stress as possible.' },
  { q: 'What should I bring to my first appointment?', a: 'Please bring your pet\'s previous vaccination records, any recent prescriptions or lab reports, a small towel or familiar blanket, and a list of questions or symptoms you have noticed at home. Dogs on a leash and cats in a carrier is safest for everyone.' },
  { q: 'How long does a consultation take?', a: 'A first consultation is usually 20 to 30 minutes so your pet has time to settle and we have time to think. Follow-ups tend to be shorter, and complex or diagnostic cases may take longer with your consent.' },
  { q: 'Do you offer emergency care?', a: 'We handle urgent cases during clinic hours between 10:30 AM and 8:30 PM. For medical emergencies outside these hours, please call ahead so we can guide you to the safest and fastest option available.' },
  { q: 'How much does a consultation cost?', a: 'We keep pricing straightforward and always share expected costs before any test or treatment. Please call or WhatsApp us for current consultation charges. There are no hidden fees added at billing.' },
  { q: 'How do I book with Dr Kaushal?', a: 'You can book directly through the Request Appointment button on this page, message us on WhatsApp at +91 9560925999 or call the clinic during opening hours. A short message with your pet\'s name and concern is usually all we need to reserve a slot.' },
];

const INTERNAL_LINKS = [
  { to: '/veterinary-clinic-noida', label: 'Veterinary Clinic in Noida' },
  { to: '/dog-vet-noida',           label: 'Dog Vet in Noida' },
  { to: '/cat-vet-noida',           label: 'Cat Vet in Noida' },
  { to: '/veterinary',              label: 'Meet Dr Kaushal' },
  { to: '/veterinary',              label: 'Pet Vaccination in Noida' },
  { to: '/veterinary',              label: 'Pet Surgery in Noida' },
  { to: '/veterinary',              label: 'Pet Diagnostics in Noida' },
  { to: '/home-veterinary-visit-noida', label: 'Home Vet Visit' },
  { to: '/book',                    label: 'Request Appointment' },
];

const PetDoctorNoida = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const orig = {
      d: document.head.querySelector('meta[name="description"]')?.getAttribute('content'),
      c: document.head.querySelector('link[rel="canonical"]')?.getAttribute('href'),
    };
    set('meta[name="description"]', 'content', DESCRIPTION);
    set('link[rel="canonical"]', 'href', CANONICAL);
    set('meta[property="og:title"]', 'content', TITLE);
    set('meta[property="og:description"]', 'content', DESCRIPTION);
    set('meta[property="og:url"]', 'content', CANONICAL);
    return () => {
      if (orig.d != null) set('meta[name="description"]', 'content', orig.d);
      if (orig.c != null) set('link[rel="canonical"]', 'href', orig.c);
    };
  }, []);

  const wa = `https://wa.me/${CONTACT.phoneRaw.replace(/\D/g, '')}?text=${encodeURIComponent("Hi, I'd like to book a consultation with the pet doctor at Ichi & Ori Pet Care.")}`;

  const faqSchema = { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQS.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) };
  const vetSchema = {
    '@context': 'https://schema.org', '@type': 'VeterinaryCare',
    name: 'Ichi and Ori Pet Care, Pet Doctor in Noida',
    url: CANONICAL, telephone: CONTACT.phoneRaw, priceRange: '₹₹',
    address: { '@type': 'PostalAddress', streetAddress: 'G11, Tower C, Noida World One', addressLocality: 'Sector 90, Noida', addressRegion: 'Uttar Pradesh', postalCode: '201305', addressCountry: 'IN' },
    aggregateRating: { '@type': 'AggregateRating', ratingValue: String(GOOGLE_RATING?.rating || 4.9), reviewCount: String(GOOGLE_RATING?.count || 107), bestRating: '5' },
    areaServed: { '@type': 'City', name: 'Noida' },
  };
  const personSchema = {
    '@context': 'https://schema.org', '@type': 'Person',
    name: 'Dr Kaushal Singh Yadav',
    jobTitle: 'Veterinarian',
    image: DR_PORTRAIT,
    worksFor: { '@type': 'VeterinaryCare', name: 'Ichi and Ori Pet Care' },
    url: CANONICAL,
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://ichiandoripetcare.com/' },
      { '@type': 'ListItem', position: 2, name: 'Pet Doctor in Noida', item: CANONICAL },
    ],
  };

  return (
    <div className="bg-white" data-testid="pet-doctor-noida-page">
      <Helmet>
        <title>{TITLE}</title>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(vetSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(personSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* 1 — HERO */}
      <section className="relative bg-gradient-to-br from-[#1a0b3d] via-[#3b1d76] to-[#5b3eb5] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_25%_20%,#e870b6_0%,transparent_40%),radial-gradient(circle_at_80%_75%,#5b3eb5_0%,transparent_45%)]" />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 py-16 sm:py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-2 sm:py-1.5 rounded-full bg-[#e870b6]/20 border border-[#e870b6]/40 text-[#ffc1e0] text-[11px] sm:text-xs font-bold tracking-[0.18em] mb-6">
              <Stethoscope className="w-3.5 h-3.5 flex-shrink-0" />TRUSTED PET DOCTOR · NOIDA
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.03] mb-6">The pet doctor Noida families call by name.</h1>
            <p className="text-base sm:text-lg text-white/85 leading-relaxed mb-9 max-w-xl">Unhurried consultations, honest advice and long-term care for dogs, cats and small pets, from Dr Kaushal and the team at Ichi &amp; Ori Pet Care in Sector 90.</p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3 sm:gap-4">
              <Link to="/book" onClick={() => trackBookButtonClick('pet_doctor_hero')} className="w-full sm:w-auto">
                <Button data-testid="pet-doctor-hero-book-btn" className="w-full sm:w-auto bg-[#e870b6] hover:bg-[#d85ba0] text-white rounded-full px-7 py-6 text-base font-semibold shadow-xl"><CalendarDays className="w-5 h-5 mr-2" />Book a Consultation</Button>
              </Link>
              <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('pet_doctor_hero')} className="w-full sm:w-auto">
                <Button data-testid="pet-doctor-hero-wa-btn" className="w-full sm:w-auto bg-[#25d366] hover:bg-[#1eb858] text-white rounded-full px-7 py-6 text-base font-semibold shadow-xl"><MessageCircle className="w-5 h-5 mr-2" />Chat on WhatsApp</Button>
              </a>
              <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('pet_doctor_hero')} className="w-full sm:w-auto sm:ml-1">
                <span className="inline-flex items-center gap-2 text-white/85 hover:text-white font-medium text-[14px] sm:text-[15px] underline decoration-white/30 underline-offset-4 hover:decoration-white transition-colors px-1 py-2">
                  <Phone className="w-4 h-4" /> Call Now
                </span>
              </a>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl border border-white/15 aspect-[4/5]">
              <img
                src={HERO_IMAGE}
                alt="A pet doctor gently examining a dog during a calm consultation in Noida — placeholder image"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <RelatedServicesNav current="/pet-doctor-noida" />

      {/* 2 — WHAT A PET DOCTOR ACTUALLY DOES */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">MORE THAN A CLINIC VISIT</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">What a good pet doctor really does.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">A trusted family veterinarian is part diagnostician, part guide and part long-term partner. These are the qualities that separate a routine appointment from care you can actually rely on.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHAT_A_PET_DOCTOR_DOES.map((r, i) => (
              <div key={i} className="bg-[#fdf7fb] rounded-2xl p-7 border border-[#f3d6f5]/60 hover:shadow-md transition-all flex flex-col h-full">
                <div className="w-12 h-12 rounded-2xl bg-white border border-[#f5d3e6] flex items-center justify-center mb-4 flex-shrink-0"><r.Icon className="w-5 h-5 text-[#e870b6]" /></div>
                <h3 className="font-bold text-[#1a0b3d] text-lg mb-2">{r.title}</h3>
                <p className="text-sm text-[#3b1d76]/85 leading-relaxed">{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 — MEET DR KAUSHAL */}
      <section className="bg-[#1a0b3d] text-white py-20 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_15%_20%,#5b3eb5_0%,transparent_45%),radial-gradient(circle_at_85%_80%,#e870b6_0%,transparent_45%)]" />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-[minmax(240px,380px)_1fr] gap-10 lg:gap-16 items-center">
          <div>
            <div className="rounded-[28px] overflow-hidden shadow-2xl border border-white/15 bg-white">
              <img
                src={cdnImg(DR_PORTRAIT, { w: 760, h: 940, fit: 'cover', a: 'center' })}
                alt="Dr Kaushal Singh Yadav, pet doctor and founding veterinarian at Ichi and Ori Pet Care, Sector 90 Noida"
                loading="lazy"
                decoding="async"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
          <div>
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#ffc1e0] mb-4">MEET THE DOCTOR</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.08] mb-6">Dr Kaushal Singh Yadav. Family pet doctor.</h2>
            <div className="space-y-5 text-white/85 text-base sm:text-[17px] leading-[1.85]">
              <p>Ichi &amp; Ori Pet Care was founded to bring back the kind of veterinary care most pet parents remember from small, familiar clinics. One doctor who knows your pet, listens carefully, and stays with the family across years.</p>
              <p>Dr Kaushal spends unhurried time with every patient, walks families through diagnosis in plain language, and builds honest plans that respect both your pet&rsquo;s wellbeing and your household&rsquo;s reality.</p>
              <p>Consultations are anchored by careful physical exams, supported by in-house diagnostics for faster answers, and always end with a clear next step and a way to reach the clinic if anything changes at home.</p>
            </div>
            <div className="mt-8">
              <Link to="/veterinary" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-semibold transition-colors">Read the full story <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* 4 — WHAT WE TREAT */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">WHAT WE TREAT</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">The care your pet doctor handles day to day.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">From first vaccinations to senior wellness, most of what a family veterinarian does is quietly preventive, thoughtfully diagnostic and always tailored to the pet in the room.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s, i) => (
              <div key={i} className="bg-[#fdf7fb] rounded-2xl p-6 border border-[#f3d6f5]/60 hover:border-[#e870b6]/50 hover:shadow-md transition-all flex flex-col h-full">
                <div className="w-11 h-11 rounded-xl bg-white border border-[#f5d3e6] flex items-center justify-center mb-4 flex-shrink-0"><s.Icon className="w-5 h-5 text-[#e870b6]" /></div>
                <h3 className="font-bold text-[#1a0b3d] text-base mb-2 leading-snug">{s.title}</h3>
                <p className="text-sm text-[#3b1d76]/80 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5 — WHEN TO VISIT A PET DOCTOR */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">KNOW WHEN TO CALL</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">When to see a pet doctor.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">Most serious conditions begin with small changes. These are the everyday signals worth acting on early, before they become bigger, harder problems.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SIGNS_TO_VISIT.map((c, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-[#f3d6f5]/60 flex flex-col h-full">
                <div className="flex items-start gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-[#fdf0f8] border border-[#f5d3e6] flex items-center justify-center flex-shrink-0"><c.Icon className="w-5 h-5 text-[#e870b6]" /></div>
                  <h3 className="font-bold text-[#1a0b3d] text-base pt-2 leading-snug">{c.title}</h3>
                </div>
                <p className="text-sm text-[#3b1d76]/80 leading-relaxed pl-[52px]">{c.text}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-[#3b1d76]/85 text-base sm:text-[17px] leading-[1.85] max-w-2xl mx-auto mt-12">If you are unsure whether a symptom needs a visit today, please reach out. A quick call or WhatsApp message often helps us decide together whether it is urgent, worth watching, or already sorted.</p>
        </div>
      </section>

      {/* 6 — LIFE STAGES */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">CARE ACROSS LIFE STAGES</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">One doctor across every chapter.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">Your pet&rsquo;s needs quietly evolve every few years. A family veterinarian adjusts the plan as they change, so care always fits the pet you have today, not the one they were last year.</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {LIFE_STAGES.map((s, i) => (
              <div key={i} className="bg-[#fdf7fb] rounded-[24px] p-8 border border-[#f3d6f5]/60 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-white border border-[#f5d3e6] flex items-center justify-center flex-shrink-0"><s.Icon className="w-5 h-5 text-[#e870b6]" /></div>
                  <span className="text-[10px] tracking-[0.22em] font-bold text-[#5b3eb5]">{s.tag}</span>
                </div>
                <h3 className="font-bold text-[#1a0b3d] text-2xl mb-3 leading-snug">{s.title}</h3>
                <p className="text-[15px] text-[#3b1d76]/85 leading-relaxed mb-5">{s.text}</p>
                <ul className="space-y-2 mt-auto">
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-[#1a0b3d] font-medium">
                      <ArrowRight className="w-4 h-4 mt-0.5 text-[#e870b6] flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7 — THE FIRST CONSULTATION */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">YOUR FIRST VISIT</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">What a consultation actually looks like.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">No white-coat rush, no jargon, no pressure. Just a calm, honest conversation about your pet, followed by a clear plan you leave with.</p>
          </div>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIRST_VISIT.map((s, i) => (
              <li key={i} className="bg-white rounded-2xl p-6 border border-[#f3d6f5]/60 flex gap-5 items-start">
                <span className="text-4xl font-extrabold text-[#e870b6]/50 leading-none tracking-tight">{s.step}</span>
                <div>
                  <h3 className="font-bold text-[#1a0b3d] text-lg mb-1.5 leading-snug">{s.title}</h3>
                  <p className="text-sm text-[#3b1d76]/85 leading-relaxed">{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 8 — WHY FAMILIES CHOOSE DR KAUSHAL */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-[minmax(240px,360px)_1fr] gap-10 lg:gap-14 items-start">
          <div>
            <div className="rounded-[24px] overflow-hidden shadow-xl border border-[#f5d3e6] bg-white">
              <img
                src={cdnImg(DR_PORTRAIT, { w: 720, h: 900, fit: 'cover', a: 'center' })}
                alt="Dr Kaushal, family pet doctor at Ichi and Ori Pet Care, Sector 90 Noida"
                loading="lazy"
                decoding="async"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
          <div>
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">WHY FAMILIES STAY WITH US</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08] mb-8">The reasons pet parents keep coming back.</h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              {WHY_FAMILIES.map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-[#fdf0f8] border border-[#f5d3e6] flex items-center justify-center flex-shrink-0 mt-0.5"><ClipboardCheck className="w-4 h-4 text-[#e870b6]" /></span>
                  <span className="text-[#1a0b3d] text-[15px] font-semibold">{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link to="/book" onClick={() => trackBookButtonClick('pet_doctor_why')} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#1a0b3d] hover:bg-[#3b1d76] text-white text-sm font-semibold transition-colors">Book with Dr Kaushal <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* 9 — AREAS SERVED */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">AREAS WE SERVE</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">The pet doctor for South Noida.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">Our clinic in Sector 90 is easy to reach from Noida Expressway and the neighbouring residential sectors, with quiet parking and short waits at consultation time.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {LOCATIONS.map((l, i) => (
              <span key={i} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#f3d6f5] text-[#1a0b3d] text-sm font-semibold">
                <MapPin className="w-3.5 h-3.5 text-[#e870b6]" /> {l}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 10 — OUR PROMISES */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">OUR PROMISES</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">The care philosophy behind every visit.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">Four commitments that shape how we consult, how we recommend and how we stay in touch between appointments.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROMISES.map((p, i) => (
              <div key={i} className="bg-[#fdf7fb] rounded-2xl p-7 border border-[#f3d6f5]/60 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#fdf3fc] to-[#f3d6f5] flex items-center justify-center mb-5 flex-shrink-0"><p.Icon className="w-5 h-5 text-[#5b3eb5]" /></div>
                <h3 className="font-serif text-[20px] font-bold text-[#1a0b3d] mb-3 leading-snug">{p.title}</h3>
                <p className="text-slate-600 text-[15px] leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 11 — FAQ */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[880px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">FREQUENTLY ASKED</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Pet doctor questions, answered honestly.</h2>
          </div>
          <Accordion type="single" collapsible defaultValue="faq-0" className="divide-y divide-[#f5d3e6] border-y border-[#f5d3e6] bg-white rounded-2xl" data-testid="pet-doctor-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="px-4">
                <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left text-[#1a0b3d] font-semibold text-base sm:text-lg hover:no-underline py-5 sm:py-6">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#3b1d76]/85 text-sm sm:text-base leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 12 — INTERNAL LINKS */}
      <section className="bg-white py-16 sm:py-20 border-y border-[#f3d6f5]/60">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-10 text-center">
          <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">EXPLORE RELATED CARE</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1a0b3d] leading-[1.1] mb-8">Related veterinary services in Noida.</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {INTERNAL_LINKS.map((l, i) => (
              <Link key={i} to={l.to} className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#fdf7fb] border border-[#f3d6f5] hover:border-[#e870b6] hover:shadow-md text-[#1a0b3d] text-sm font-semibold transition-all">
                {l.label} <ArrowRight className="w-4 h-4 text-[#e870b6] group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 13 — FINAL CTA */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="rounded-[32px] bg-gradient-to-br from-[#5b3eb5] to-[#1a0b3d] text-white py-16 sm:py-20 px-6 relative overflow-hidden">
            <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_20%_30%,#e870b6_0%,transparent_45%)]" />
            <div className="relative text-center">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 leading-[1.08]">Book your pet&rsquo;s next visit with a doctor who remembers them.</h2>
              <p className="text-white/85 mb-10 max-w-xl mx-auto text-base sm:text-lg leading-relaxed">Whether it is a first consultation, a booster review or something that just does not feel right, we are here to listen, examine and help you decide the best next step for your pet.</p>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4">
                <Link to="/book" onClick={() => trackBookButtonClick('pet_doctor_final')} className="w-full sm:w-auto">
                  <Button data-testid="pet-doctor-final-book-btn" className="w-full sm:w-auto bg-[#e870b6] hover:bg-[#d85ba0] rounded-full px-8 py-6 font-semibold shadow-xl"><CalendarDays className="w-5 h-5 mr-2" />Request Appointment</Button>
                </Link>
                <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('pet_doctor_final')} className="w-full sm:w-auto">
                  <Button data-testid="pet-doctor-final-wa-btn" className="w-full sm:w-auto bg-[#25d366] hover:bg-[#1eb858] rounded-full px-8 py-6 font-semibold shadow-xl"><MessageCircle className="w-5 h-5 mr-2" />WhatsApp</Button>
                </a>
                <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('pet_doctor_final')} className="w-full sm:w-auto sm:ml-1">
                  <span className="inline-flex items-center gap-2 text-white/85 hover:text-white font-medium text-[14px] sm:text-[15px] underline decoration-white/30 underline-offset-4 hover:decoration-white transition-colors px-1 py-2">
                    <Phone className="w-4 h-4" /> Call Now
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      <StickyMobileCta source="petdoctor" />
    </div>
  );
};

export default PetDoctorNoida;
